/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../api";
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";

export default function ReviewInsight() {
  const { id } = useParams();
  const numericId = Number(id);
  const navigate = useNavigate();
  const { token } = useAuth();

  const [insight, setInsight] = useState(null);
  const [rawTranscript, setRawTranscript] = useState("");
  const [translatedTranscript, setTranslatedTranscript] = useState("");

  const [intent, setIntent] = useState("");
  const [confidence, setConfidence] = useState("");
  const [structuredData, setStructuredData] = useState("{}");

  const [status, setStatus] = useState("idle"); // idle | loading | saving | error | success

  useEffect(() => {
    const fetchInsight = async () => {
      setStatus("loading");
      try {
        const res = await api.get(`/api/secure/my-insights`);
        const found = res.data.find((i) => i.id === numericId);
        if (!found) throw new Error("Insight not found");

        setInsight(found);
        setRawTranscript(found.raw_transcript || "");
        setTranslatedTranscript(found.translated_transcript || "");

        // Safely unpack structured_data
        const sd = found.structured_data || {};
        setIntent(sd.intent || "");
        setConfidence(sd.confidence || "");
        setStructuredData(JSON.stringify(sd.data || {}, null, 2));

        setStatus("idle");
      } catch (err) {
        console.error("Failed to load insight", err);
        setStatus("error");
      }
    };

    fetchInsight();
  }, [id]);

  const handleSave = async () => {
    setStatus("saving");
    try {
      const parsedData = JSON.parse(structuredData);

      const payload = {
        raw_transcript: rawTranscript,
        translated_transcript: translatedTranscript,
        structured_data: {
          intent,
          confidence,
          data: parsedData,
        },
      };

      await api.patch(`/api/secure/field-insights/${id}`, payload);

      setStatus("success");
      setTimeout(() => navigate("/secure/my-insights"), 1500);
    } catch (err) {
      console.error("Failed to update insight", err);
      setStatus("error");
    }
  };

  if (status === "loading") return <CircularProgress />;
  if (status === "error")
    return <Alert severity="error">Failed to load insight.</Alert>;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        This insight was auto-generated by AI. You can correct or refine it before using it elsewhere.
      </Typography>

      <Typography variant="h5" gutterBottom>
        ✍️ Correct AI-Generated Field Insight
      </Typography>

      {/* AI Interpretation Block */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          mb: 2,
          p: 2,
          backgroundColor: "#f7f7f7",
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          AI Interpretation
        </Typography>

        <Box display="flex" gap={2} mt={1}>
          <TextField
            label="Intent"
            size="small"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            helperText="e.g. supplier_onboarding, price_intelligence, quality_issue"
            fullWidth
          />

          <TextField
            label="Confidence"
            size="small"
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
            helperText="low | medium | high"
            fullWidth
          />
        </Box>
      </Paper>

      {insight?.llm_prompt_used && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Prompt used:
          </Typography>
          <pre
            style={{
              background: "#f5f5f5",
              padding: "8px",
              borderRadius: 4,
              overflowX: "auto",
            }}
          >
            {insight.llm_prompt_used}
          </pre>
        </Box>
      )}

      <Box mt={2}>
        <TextField
          label="Raw Transcript"
          fullWidth
          multiline
          rows={3}
          value={rawTranscript}
          onChange={(e) => setRawTranscript(e.target.value)}
        />
      </Box>

      <Box mt={2}>
        <TextField
          label="Translated Transcript"
          fullWidth
          multiline
          rows={3}
          value={translatedTranscript}
          onChange={(e) => setTranslatedTranscript(e.target.value)}
        />
      </Box>

      <Box mt={2}>
        <TextField
          label="Structured Data (extracted fields)"
          fullWidth
          multiline
          rows={8}
          value={structuredData}
          onChange={(e) => setStructuredData(e.target.value)}
          helperText="Edit only the extracted data for this intent. Must be valid JSON."
        />
      </Box>

      <Box mt={2} display="flex" gap={2}>
        <Button variant="contained" onClick={handleSave}>
          Save Changes
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate("/secure/my-insights")}
        >
          Cancel
        </Button>
      </Box>

      {status === "saving" && <p>⏳ Saving changes...</p>}
      {status === "success" && <p>✅ Updated successfully!</p>}
    </Box>
  );
}